// ============================================================================
// Component-scoped daily-photo URL owner
// ============================================================================
//
// Every visible DailyPhotoThumbnails mount renders private daily-photos bucket
// objects. The presenter must not mint those URLs in an effect: this owner
// gives each allowed path an authenticated, role/preview-scoped Query key,
// dedupes the same object across mounted records, and refreshes before the
// server's ten-minute URL expires. It is intentionally not a route resource;
// its finite set of reachable mounts is recorded in dailyPhotoUrlRegistry.
// ============================================================================

import {useQueries} from '@tanstack/react-query';
import type {QueryKey as TanStackQueryKey, UseQueryOptions} from '@tanstack/react-query';
import {useMemo} from 'react';

import type {AuthState} from '../contexts/AuthContext.jsx';
import {DAILY_BUCKET, isValidFormKind} from '../lib/dailyPhotos.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {resourceKey, scopeOrNull} from '../app/data/routeDataCatalog.js';
import type {AuthenticatedScope} from '../app/data/routeDataCatalog.js';
import {DAILY_PHOTO_URL_RESOURCE} from './dailyPhotoUrlRegistry.js';

// This remains the server contract established by dailyPhotos: URLs are
// minted for ten minutes and are never stored back into a daily row.
export const DAILY_PHOTO_SIGNED_URL_TTL_SECONDS = 600;
export const DAILY_PHOTO_SIGNED_URL_TTL_MS = DAILY_PHOTO_SIGNED_URL_TTL_SECONDS * 1000;

// Sign again before a visible image can turn into a broken URL. The deliberate
// lead also gives slow farm connections a window to complete the replacement.
export const DAILY_PHOTO_SIGNED_URL_REFRESH_LEAD_MS = 30 * 1000;
export const DAILY_PHOTO_SIGNED_URL_REFRESH_AFTER_MS =
  DAILY_PHOTO_SIGNED_URL_TTL_MS - DAILY_PHOTO_SIGNED_URL_REFRESH_LEAD_MS;
const MIN_REFRESH_DELAY_MS = 1_000;

// This allowlist is the same path vocabulary dailyPhotos.buildPhotoPath writes.
// It keeps untrusted jsonb metadata from selecting an arbitrary private object
// in the bucket while allowing historic photo filenames inside a valid report.
export type DailyPhotoUrlInput = {
  readonly path?: unknown;
};

export type NormalizedDailyPhotoUrlInput = {
  readonly cacheKey: string;
  readonly bucket: typeof DAILY_BUCKET;
  readonly path: string;
};

export type DailyPhotoSignedUrl = {
  readonly url: string;
  // Captured before signing starts. A slow request must not extend the URL's
  // lifetime by pretending it was minted when the response arrived.
  readonly issuedAt: number;
  readonly expiresAt: number;
};

export type DailyPhotoUrlProjection = {
  readonly url: string | null;
  readonly loading: boolean;
  readonly error: unknown;
};

export type DailyPhotoUrlMap = Readonly<Record<string, DailyPhotoUrlProjection>>;

const EMPTY_DAILY_PHOTO_URL: DailyPhotoUrlProjection = Object.freeze({url: null, loading: false, error: null});

type DailyPhotoUrlQueryOption = UseQueryOptions<DailyPhotoSignedUrl, Error, DailyPhotoSignedUrl, TanStackQueryKey>;

type DailyPhotoUrlQueryLike = {
  readonly data: DailyPhotoSignedUrl | undefined;
  readonly isPending: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
};

function isAllowedDailyPhotoPath(path: string): boolean {
  const segments = path.split('/');
  return (
    segments.length === 3 &&
    isValidFormKind(segments[0]) &&
    segments.every((segment) => Boolean(segment) && segment !== '.' && segment !== '..')
  );
}

/**
 * Normalize jsonb photo metadata into the exact allowed storage selector and
 * cache identity. There is one fixed private bucket; only a canonical daily
 * report path can reach it, and duplicate rows share `${bucket}:${path}`.
 */
export function normalizeDailyPhotoUrlInput(
  photo: DailyPhotoUrlInput | null | undefined,
): NormalizedDailyPhotoUrlInput | null {
  const path = typeof photo?.path === 'string' ? photo.path.trim() : '';
  if (!path || !isAllowedDailyPhotoPath(path)) return null;
  return {cacheKey: `${DAILY_BUCKET}:${path}`, bucket: DAILY_BUCKET, path};
}

/** Collect unique signable paths while retaining the presenter's original rows. */
export function collectDailyPhotoUrlInputs(
  photos: readonly DailyPhotoUrlInput[] | null | undefined,
): readonly NormalizedDailyPhotoUrlInput[] {
  const byCacheKey = new Map<string, NormalizedDailyPhotoUrlInput>();
  for (const photo of photos || []) {
    const normalized = normalizeDailyPhotoUrlInput(photo);
    if (normalized) byCacheKey.set(normalized.cacheKey, normalized);
  }
  return [...byCacheKey.values()];
}

/** Daily-photo URLs never run under a public or unresolved identity. */
export function dailyPhotoScope(authState: AuthState, rolePreview?: unknown): AuthenticatedScope | null {
  const scope = scopeOrNull(authState, rolePreview);
  return scope && scope.kind === 'authenticated' ? scope : null;
}

function throwIfAborted(signal: AbortSignal): void {
  // Supabase storage's signing API has no AbortSignal parameter. Reading Query's
  // signal still lets Query retire this observer; the post-await check stops an
  // abandoned signing result from becoming usable in an old scope's entry.
  if (signal.aborted) throw new Error('Daily-photo signing was aborted.');
}

/** The one physical automatic signing call for every visible daily thumbnail. */
export async function loadDailyPhotoSignedUrl(
  sb: SupabaseClient,
  photo: NormalizedDailyPhotoUrlInput,
  signal: AbortSignal,
  now: () => number = Date.now,
): Promise<DailyPhotoSignedUrl> {
  throwIfAborted(signal);
  const issuedAt = now();
  const {data, error} = await sb.storage
    .from(DAILY_BUCKET)
    .createSignedUrl(photo.path, DAILY_PHOTO_SIGNED_URL_TTL_SECONDS);
  throwIfAborted(signal);
  if (error) throw new Error(error.message || 'Could not load daily photo.');
  if (!data?.signedUrl) throw new Error('Could not load daily photo.');
  return {url: data.signedUrl, issuedAt, expiresAt: issuedAt + DAILY_PHOTO_SIGNED_URL_TTL_MS};
}

/** Time until the next pre-expiry refresh, based on the URL's true issuance. */
export function dailyPhotoRefreshDelay(
  data: DailyPhotoSignedUrl | undefined,
  now: number = Date.now(),
): number | false {
  if (!data || !data.url || !Number.isFinite(data.expiresAt)) return false;
  return Math.max(MIN_REFRESH_DELAY_MS, data.expiresAt - now - DAILY_PHOTO_SIGNED_URL_REFRESH_LEAD_MS);
}

/** Never expose an expired signed URL to an image element. */
export function dailyPhotoUrlProjection(
  query: DailyPhotoUrlQueryLike,
  now: number = Date.now(),
): DailyPhotoUrlProjection {
  const data = query.data;
  const url = data && data.expiresAt > now ? data.url : null;
  return {
    url,
    loading: Boolean(!url && (query.isPending || query.isFetching)),
    error: query.error ?? null,
  };
}

/** The presenter's safe fallback for a blank, malformed, or unobserved path. */
export function dailyPhotoUrlFor(
  urls: DailyPhotoUrlMap,
  photo: DailyPhotoUrlInput | null | undefined,
): DailyPhotoUrlProjection {
  const normalized = normalizeDailyPhotoUrlInput(photo);
  return normalized ? urls[normalized.cacheKey] || EMPTY_DAILY_PHOTO_URL : EMPTY_DAILY_PHOTO_URL;
}

function dailyPhotoQueryKey(scope: AuthenticatedScope, photo: NormalizedDailyPhotoUrlInput) {
  return resourceKey(DAILY_PHOTO_URL_RESOURCE.SIGNED_URL, scope, {
    bucket: photo.bucket,
    path: photo.path,
    ttlSeconds: DAILY_PHOTO_SIGNED_URL_TTL_SECONDS,
  });
}

/**
 * Component-scoped owner for automatic URL reads. It is called once per
 * DailyPhotoThumbnails mount; individual thumbnail buttons are pure presenters
 * for the automatic path and keep their existing fresh click-open action.
 */
export function useDailyPhotoUrls({
  sb,
  authState,
  rolePreview,
  photos,
}: {
  sb: SupabaseClient;
  authState: AuthState;
  rolePreview?: unknown;
  photos: readonly DailyPhotoUrlInput[] | null | undefined;
}): DailyPhotoUrlMap {
  const scope = useMemo(() => dailyPhotoScope(authState, rolePreview), [authState, rolePreview]);
  const signablePhotos = useMemo(() => collectDailyPhotoUrlInputs(photos), [photos]);
  const queryOptions = useMemo<readonly DailyPhotoUrlQueryOption[]>(() => {
    // A signed URL is private. Do not leave even an inert shared public key for
    // unresolved/signed-out identity: render no observers until scope settles.
    if (!scope) return [];
    return signablePhotos.map((photo) => ({
      queryKey: dailyPhotoQueryKey(scope, photo),
      // Destructuring signal is load-bearing. Query only retires an orphaned
      // request when the function reads it, and the helper checks around the
      // SDK call whose API itself does not accept a signal.
      queryFn: ({signal}: {signal: AbortSignal}) => loadDailyPhotoSignedUrl(sb, photo, signal),
      // This is a server-URL lifetime, not a farm-data caching decision. A
      // duplicate thumbnail shares one valid URL, then refreshes before expiry.
      staleTime: DAILY_PHOTO_SIGNED_URL_REFRESH_AFTER_MS,
      refetchInterval: (query: {state: {data: DailyPhotoSignedUrl | undefined}}) =>
        dailyPhotoRefreshDelay(query.state.data),
      // A background tab does not continuously mint private URLs. On return,
      // Query refetches only if the pre-expiry stale boundary has passed.
      refetchOnWindowFocus: true,
    }));
  }, [sb, scope, signablePhotos]);
  const queries = useQueries({queries: queryOptions});

  return useMemo(() => {
    const urls: Record<string, DailyPhotoUrlProjection> = {};
    for (let index = 0; index < signablePhotos.length; index += 1) {
      urls[signablePhotos[index].cacheKey] = dailyPhotoUrlProjection(queries[index]);
    }
    return urls;
  }, [queries, signablePhotos]);
}
